import { Alert, Box, CircularProgress, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import { base64UrlToUint8Array } from "../utils/base64";

const ClientPwaNotificationsForm = () => {
  const { authorizedFetch } = useAuth();
  const { pwaPushPublicKey } = useAppContext();
  const [pushPublicKey, setPushPublicKey] = useState<string | null>(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushInitialized, setPushInitialized] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );

  const pushSupported = useMemo(
    () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined",
    [],
  );

  useEffect(() => {
    setPushPublicKey(pwaPushPublicKey);
  }, [pwaPushPublicKey]);

  useEffect(() => {
    if (!pushSupported) {
      return;
    }
    let cancelled = false;

    const syncSubscriptionState = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!cancelled) {
          setPushEnabled(Boolean(subscription));
          setPushInitialized(true);
        }
      } catch (error) {
        if (!cancelled) {
          setPushError(error instanceof Error ? error.message : "Unable to determine push subscription state");
        }
      }
    };

    void syncSubscriptionState();
    return () => {
      cancelled = true;
    };
  }, [pushSupported]);

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setPushPermission(Notification.permission);
    }
  }, []);

  const requestNotificationPermission = async () => {
    if (typeof Notification === "undefined") {
      return "default" as NotificationPermission;
    }
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      return permission;
    }
    setPushPermission(Notification.permission);
    return Notification.permission;
  };

  const enablePushNotifications = async () => {
    if (!pushSupported) {
      setPushError("This browser does not support push notifications.");
      return;
    }
    if (!pushPublicKey) {
      setPushError("Push notifications are not configured on the server.");
      return;
    }
    if (typeof Notification !== "undefined" && Notification.permission === "denied") {
      setPushPermission(Notification.permission);
      setPushError("Notifications are blocked for this site. Update your browser permissions to continue.");
      return;
    }

    setPushBusy(true);
    setPushError(null);
    try {
      const permission = await requestNotificationPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission was not granted");
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const serverKey = base64UrlToUint8Array(pushPublicKey);
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: serverKey.buffer as ArrayBuffer,
        });
      }

      const payload = subscription.toJSON();
      const response = await authorizedFetch("/api/pwa/push-subscriptions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Unable to register this device for push notifications");
      }

      setPushEnabled(true);
    } catch (error) {
      setPushError(error instanceof Error ? error.message : "Unable to enable push notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const disablePushNotifications = async () => {
    if (!pushSupported) {
      return;
    }

    setPushBusy(true);
    setPushError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await authorizedFetch("/api/pwa/push-subscriptions", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        }).catch(() => undefined);
        await subscription.unsubscribe();
      }
      setPushEnabled(false);
    } catch (error) {
      setPushError(error instanceof Error ? error.message : "Unable to disable push notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const handlePushToggle = (_: unknown, checked: boolean) => {
    if (pushBusy) {
      return;
    }
    if (checked) {
      void enablePushNotifications();
    } else {
      void disablePushNotifications();
    }
  };

  const pushToggleDisabled = !pushSupported || !pushPublicKey || pushBusy;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Background notifications let you keep receiving server stream alerts even when the application window is closed.
      </Typography>
      {!pushSupported && <Alert severity="warning">This browser does not support push notifications.</Alert>}
      {pushSupported && !pushPublicKey && (
        <Alert severity="warning">The server has not been configured with Web Push keys yet.</Alert>
      )}
      {pushSupported && (
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={pushEnabled}
                onChange={handlePushToggle}
                disabled={pushToggleDisabled}
                name="clientPushNotifications"
              />
            }
            label={
              pushBusy ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <span>Updating device subscription…</span>
                </Box>
              ) : (
                "Enable push notifications on this device"
              )
            }
          />
          <Typography variant="caption" color="text.secondary">
            Notification permission: {pushPermission}
            {!pushInitialized && pushSupported ? " (detecting…)" : ""}
          </Typography>
          {pushError && <Alert severity="error">{pushError}</Alert>}
          {pushPermission === "denied" && (
            <Alert severity="warning">
              Notifications are blocked for this site. Change the browser permission to turn push notifications back on.
            </Alert>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ClientPwaNotificationsForm;
